import { locales, type Locale } from "@/i18n/locales";

export interface FaqItem {
  question: string;
  answer: string;
}

export interface BreadcrumbItem {
  name: string;
  item?: string;
}

export type SupportedLocale = Locale;

type BuildPageMetadataOptions = {
  baseUrl: string;
  locale: SupportedLocale;
  path: string;
  title: string;
  description: string;
  type?: "website" | "article";
  /**
   * Locales this page is actually published in. hreflang is only emitted for
   * these; omit it for pages that exist in a single language (hreflang is an
   * extension of rel=alternate, so a page with no alternate has nothing to
   * declare).
   */
  availableLocales?: readonly SupportedLocale[];
  images?: Array<{
    url: string;
    width?: number;
    height?: number;
    alt?: string;
  }>;
};

const ALL_LOCALES: readonly SupportedLocale[] = locales;

export const ORGANIZATION_SAME_AS = [
  "https://www.producthunt.com/products/tryschedule?embed=true&utm_source=badge-featured&utm_medium=badge&utm_campaign=badge-tryschedule",
  "https://aiagentsdirectory.com/agent/tryschedule?utm_source=badge&utm_medium=referral&utm_campaign=free_listing&utm_content=tryschedule",
] as const;

function buildLocalizedUrl(
  baseUrl: string,
  path: string,
  locale: SupportedLocale,
) {
  if (path === "/") {
    return locale === "en" ? baseUrl : `${baseUrl}/${locale}`;
  }

  return locale === "en" ? `${baseUrl}${path}` : `${baseUrl}/${locale}${path}`;
}

export function buildLocalizedAlternates(
  baseUrl: string,
  path: string,
  locale: SupportedLocale,
  availableLocales: readonly SupportedLocale[] = ALL_LOCALES,
) {
  const canonical = buildLocalizedUrl(baseUrl, path, locale);

  // hreflang declares alternate language versions. A page published in a
  // single language has no alternates, so it gets a self-referencing canonical
  // and no hreflang at all.
  const languages: Record<string, string> = {};
  if (availableLocales.length > 1) {
    for (const available of availableLocales) {
      languages[available] = buildLocalizedUrl(baseUrl, path, available);
    }
    if (languages.en) {
      languages["x-default"] = languages.en;
    }
  }

  return { canonical, languages };
}

export function buildAbsoluteUrl(baseUrl: string, pathOrUrl: string): string {
  if (/^https?:\/\//i.test(pathOrUrl)) {
    return pathOrUrl;
  }

  return `${baseUrl}${pathOrUrl.startsWith("/") ? pathOrUrl : `/${pathOrUrl}`}`;
}

export function buildPageMetadata({
  baseUrl,
  locale,
  path,
  title,
  description,
  type = "website",
  availableLocales,
  images = [
    {
      url: `${baseUrl}/opengraph-image.png`,
      width: 1200,
      height: 630,
      alt: "TrySchedule - Free Online Schedule Builder",
    },
  ],
}: BuildPageMetadataOptions) {
  const canonical = buildLocalizedUrl(baseUrl, path, locale);

  return {
    title,
    description,
    alternates: buildLocalizedAlternates(baseUrl, path, locale, availableLocales),
    openGraph: {
      title,
      description,
      url: canonical,
      type,
      locale,
      siteName: "TrySchedule",
      images,
    },
    twitter: {
      card: "summary_large_image" as const,
      title,
      description,
      images: images.map((image) => image.url),
    },
  };
}

export function buildWebSiteSchema(
  baseUrl: string,
  inLanguage: SupportedLocale,
) {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "TrySchedule",
    url: baseUrl,
    inLanguage,
  };
}

export interface ProductOffer {
  name: string;
  price: string;
  /**
   * Billing period for subscriptions, e.g. "P1M" for a monthly plan. When set,
   * the offer carries a UnitPriceSpecification so a recurring plan is not
   * silently downgraded to a one-time price.
   */
  billingDuration?: string;
}

/**
 * Product schema for the pricing page: one Product with one Offer per plan.
 *
 * A digital product carries no shippingDetails/hasMerchantReturnPolicy (those
 * describe physical fulfillment), and priceValidUntil is omitted — Google
 * treats it as optional and any fixed date goes stale without a rebuild.
 */
export function buildProductSchema({
  name,
  description,
  image,
  url,
  offers,
}: {
  name: string;
  description: string;
  image: string;
  url: string;
  offers: ProductOffer[];
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name,
    description,
    image,
    url,
    brand: {
      "@type": "Brand",
      name: "TrySchedule",
    },
    offers: offers.map((offer) => ({
      "@type": "Offer",
      name: offer.name,
      price: offer.price,
      priceCurrency: "USD",
      availability: "https://schema.org/InStock",
      ...(offer.billingDuration
        ? {
            priceSpecification: {
              "@type": "UnitPriceSpecification",
              price: offer.price,
              priceCurrency: "USD",
              billingDuration: offer.billingDuration,
            },
          }
        : {}),
    })),
  };
}

export function buildFaqSchema(faqs: FaqItem[], inLanguage?: SupportedLocale) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    ...(inLanguage ? { inLanguage } : {}),
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };
}

export function buildHowToSchema(
  title: string,
  steps: string[],
  inLanguage?: SupportedLocale,
  description?: string,
  totalTime?: string,
) {
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: title,
    ...(inLanguage ? { inLanguage } : {}),
    ...(description ? { description } : {}),
    ...(totalTime ? { totalTime } : {}),
    step: steps.map((stepText, index) => ({
      "@type": "HowToStep",
      name: inLanguage === "es" ? `Paso ${index + 1}` : `Step ${index + 1}`,
      text: stepText,
    })),
  };
}

export function buildBreadcrumbSchema(items: BreadcrumbItem[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      ...(item.item ? { item: item.item } : {}),
    })),
  };
}
